<template>
  <div v-if="isOpen" class="fixed inset-0 z-[9999] overflow-y-auto" @click.self="close">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity z-[9998]" style="background-color: rgba(0, 0, 0, 0.55);" @click="close"></div>
      
      <!-- Command Palette -->
      <div class="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl relative z-[9999]">
        <!-- Header -->
        <div class="px-6 py-4 bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="p-2 bg-green-600 rounded-lg">
                <svg class="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-semibold text-gray-900">Command Palette</h3>
                <p class="text-sm text-gray-600">Search and navigate quickly</p>
              </div>
            </div>
            <button @click="close" class="text-gray-400 hover:text-gray-600 transition-colors">
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Search Input -->
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="relative">
            <div class="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              ref="searchInput"
              v-model="query"
              type="text"
              placeholder="Ask anything about your supply chain..."
              class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
              @keydown.enter="executeQuery"
              @keydown.esc="close"
            />
          </div>
        </div>

        <!-- Quick Actions -->
        <div v-if="!query" class="px-6 py-4">
          <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Quick Actions</div>
          <div class="space-y-1">
            <button
              v-for="action in quickActions"
              :key="action.label"
              @click="selectAction(action)"
              class="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <div class="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" :class="action.bgColor">
                <svg class="w-4 h-4" :class="action.iconColor" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="action.icon" />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-gray-900">{{ action.label }}</div>
                <div class="text-xs text-gray-500">{{ action.description }}</div>
              </div>
            </button>
          </div>
        </div>

        <!-- Recent Queries -->
        <div v-if="!query && recentQueries.length > 0" class="border-t border-gray-200 px-6 py-4">
          <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Recent Queries</div>
          <div class="space-y-1">
            <button
              v-for="(recentQuery, index) in recentQueries"
              :key="index"
              @click="query = recentQuery; executeQuery()"
              class="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div class="text-sm text-gray-700 truncate">{{ recentQuery }}</div>
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="border-t border-gray-200 px-6 py-3 bg-gray-50 rounded-b-lg">
          <div class="flex items-center justify-between text-xs text-gray-500">
            <div class="flex items-center space-x-4">
              <span class="flex items-center">
                <kbd class="px-2 py-1 bg-white border border-gray-200 rounded mr-1 font-mono">↵</kbd>
                Execute
              </span>
              <span class="flex items-center">
                <kbd class="px-2 py-1 bg-white border border-gray-200 rounded mr-1 font-mono">ESC</kbd>
                Close
              </span>
            </div>
            <div class="text-gray-400">Powered by Domain Graph</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CommandPalette',
  
  data() {
    return {
      isOpen: false,
      query: '',
      recentQueries: [],
      quickActions: [
        {
          label: 'Search farms & wool lines',
          description: 'Find farms, wool lines, and consignments',
          icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
          bgColor: 'bg-green-100',
          iconColor: 'text-green-600',
          action: 'search'
        },
        {
          label: 'Trace supply chain',
          description: 'Follow entities through Domain Graph',
          icon: 'M13 10V3L4 14h7v7l9-11h-7z',
          bgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          action: 'trace'
        },
        {
          label: 'Find connections',
          description: 'Discover relationships in Domain Graph',
          icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
          bgColor: 'bg-purple-100',
          iconColor: 'text-purple-600',
          action: 'connections'
        },
        {
          label: 'View Domain Graph',
          description: 'Explore the knowledge graph',
          icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
          bgColor: 'bg-amber-100',
          iconColor: 'text-amber-600',
          action: 'graph'
        }
      ]
    };
  },

  mounted() {
    // Add keyboard listener for Cmd+K / Ctrl+K
    document.addEventListener('keydown', this.handleKeydown);
    
    // Load recent queries from localStorage
    const saved = localStorage.getItem('agricore_recent_queries');
    if (saved) {
      this.recentQueries = JSON.parse(saved);
    }
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    handleKeydown(e) {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        this.toggle();
      }
    },

    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },

    open() {
      this.isOpen = true;
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },

    close() {
      this.isOpen = false;
      this.query = '';
    },

    selectAction(action) {
      switch (action.action) {
        case 'search':
          // Navigate to main search page
          window.location.href = '/';
          this.close();
          break;
        case 'trace':
          this.query = 'Trace bale ';
          this.$refs.searchInput?.focus();
          break;
        case 'connections':
          this.query = 'Show connections for bale ';
          this.$refs.searchInput?.focus();
          break;
        case 'graph':
          this.navigateToDomainGraph();
          break;
      }
    },

    executeQuery() {
      if (!this.query.trim()) return;

      // Add to recent queries
      this.addToRecentQueries(this.query);

      // Detect if query is for Atralis search (farms, wool lines, consignments from Supabase)
      // or Domain Graph (bales, containers, etc.)
      const atraliskeywords = ['farm', 'wool line', 'pic', 'koo-', 'nsw', 'vic', 'qld', 'sa', 'wa', 'tas'];
      const domainGraphKeywords = ['bale', 'ibg-', 'container', 'sale lot', 'test result', 'wtl-'];
      
      const queryLower = this.query.toLowerCase();
      const isAtralissearch = atraliskeywords.some(keyword => queryLower.includes(keyword));
      const isDomainGraph = domainGraphKeywords.some(keyword => queryLower.includes(keyword));

      if (isAtralissearch && !isDomainGraph) {
        // Navigate to main search page - let user search there
        window.location.href = '/';
        this.close();
      } else {
        // Navigate to domain graph page with query
        this.navigateToDomainGraph(this.query);
      }
    },

    addToRecentQueries(query) {
      // Remove if already exists
      this.recentQueries = this.recentQueries.filter(q => q !== query);
      
      // Add to beginning
      this.recentQueries.unshift(query);
      
      // Keep only last 5
      this.recentQueries = this.recentQueries.slice(0, 5);
      
      // Save to localStorage
      localStorage.setItem('agricore_recent_queries', JSON.stringify(this.recentQueries));
    },

    navigateToDomainGraph(query = null) {
      // Store query in sessionStorage for domain graph to pick up
      if (query) {
        sessionStorage.setItem('domain_graph_query', query);
      }
      
      // Navigate to domain graph page
      window.location.href = '/domain-graph';
      
      this.close();
    }
  }
};
</script>

<style scoped>
/* Modal fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
